import React, { useMemo } from 'react';
import useResourceLoader from '../util/useResourceLoader';
import UWEEntityApi from '../api/UWEEntityApi';
import Loader from '../components/Loader';
import useLoader from '../util/useLoader';
import { useOpenModal } from '../components/FormModal';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import UserIcon from '../components/UserIcon';
import { checkAccess } from '../util/useAccessCheck';

const CONFIRM_ASSIGNMENT_MODAL_DEF = (user, role) => [
  'ConfirmationModal',
  t`Confirm assignment of ${role?.name}`,
  t`Are you sure you want to assign user ${user.name}?`,
  [
    { text: t`Yes`, color: 'primary', value: true },
    { text: t`No`, color: 'secondary', value: false },
  ],
];

function UWEEntityAssignments({ entity, auth, wfAccessToken }) {
  const entityId = entity?.id;

  const openModal = useOpenModal();

  const [assignmentByRoleIdMap, loadingAssignments, , setAssignmentByRoleIdMap] = useResourceLoader(
    async () =>
      (await UWEEntityApi.getUWEEntityAssignments({ id: entityId, wfAccessToken })).assignments.reduce((_, x) => {
        _[x.roleId] = x;

        return _;
      }, {}),
    [entityId],
    []
  );

  const [{ roles, users, orgs, roleUsers }, loadingRoles, errorRoles] = useResourceLoader(
    () => UWEEntityApi.getWorkflowRoles({ id: entityId, wfAccessToken }),
    [entityId],
    {}
  );

  const groupings = useMemo(
    () =>
      (orgs || []).map(o => ({
        ...o,
        userIds: new Set(o.userIds),
      })),
    [orgs]
  );

  const usersById = useMemo(
    () =>
      (users || []).reduce((_, user) => {
        _[user.id] = user;

        return _;
      }, {}),
    [users]
  );

  const assignments = useMemo(
    () =>
      (roles || []).map(role => ({
        role,
        user: assignmentByRoleIdMap[role.id]?.user,
        userOptions: (roleUsers[role.id] || []).map(userId => usersById[userId]).filter(x => !!x),
      })),
    [roles, assignmentByRoleIdMap, usersById, roleUsers]
  );

  async function assignUser(roleId, userId, uweEntityId) {
    const user = usersById[userId];
    uweEntityId = uweEntityId ?? entityId;
    if (!user) return;
    const role = roles.find(x => x.id === roleId);

    if (await openModal(...CONFIRM_ASSIGNMENT_MODAL_DEF(user, role))) {
      await UWEEntityApi.setUWEEntityAssignment({ id: uweEntityId, roleId, userId, wfAccessToken });
      setAssignmentByRoleIdMap(assignmentByRoleIdMap => ({
        ...assignmentByRoleIdMap,
        [roleId]: {
          ...assignmentByRoleIdMap[roleId],
          user,
        },
      }));
    }
  }

  return loadingAssignments || loadingRoles ? (
    <Loader fullscreen>
      <Trans>Loading Roles and Assignments</Trans>
    </Loader>
  ) : (
    <div className="assignments-list">
      {assignments.map(({ uweEntityId, role, user, userOptions }, idx) =>
        user ? (
          <RoleAssignmentSelect
            key={idx}
            roleName={role?.name}
            user={user}
            groupings={groupings}
            onChange={userId => assignUser(role.id, userId, uweEntityId)}
            userOptions={userOptions}
            authUser={auth}
          />
        ) : null
      )}
    </div>
  );
}

function RoleAssignmentSelect({ roleName, user, userOptions, groupings, onChange: propOnChange, authUser }) {
  const [loading, error, loadFn] = useLoader();

  function onChange({ target: { value } }) {
    loadFn(() => propOnChange(value));
  }

  const userInOptions = useMemo(() => user && userOptions && userOptions.some(u => u.id === user.id), [user, userOptions]);
  const userCanAssign = !!checkAccess(authUser, roleName, 'canAssign');

  return (
    <div className="assignment-row">
      <div className="assignment-role-label">{roleName}</div>
      {loading ? (
        <div className="assignment-loading">
          <Loader>
            <Trans>Updating...</Trans>
          </Loader>
        </div>
      ) : (
        <div className="assignment-user-row">
          <div className="assignment-avatar">
            <UserIcon user={user} size={25} />
          </div>
          <select className="assignment-select" value={user?.id} disabled={!userCanAssign} readOnly={!userCanAssign} onChange={onChange}>
            {!user ? <option value={0}>--------------</option> : null}
            {user && !userInOptions ? <option value={user.id}>{user.name || `User #${user.id}`}</option> : null}
            {groupings
              ? groupings.map((grp, idx) => {
                  const groupUsers = (userOptions || []).filter(u => grp.userIds.has(u.id));

                  return groupUsers.length ? (
                    <optgroup key={idx} label={grp.name}>
                      {groupUsers.map(({ id, name }, idx) => (
                        <option key={idx} value={id}>
                          {name}
                        </option>
                      ))}
                    </optgroup>
                  ) : null;
                })
              : (userOptions || []).map(({ id, name }, idx) => (
                  <option key={idx} value={id}>
                    {name}
                  </option>
                ))}
          </select>
        </div>
      )}
    </div>
  );
}

export default UWEEntityAssignments;
